import React, { Component } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Steps,
  message,
  Space,
  Card
} from "antd";
import { PlusOutlined, UserOutlined, MailOutlined, PhoneOutlined, DeleteOutlined } from "@ant-design/icons";
import API from "../../API/API";

const { Option } = Select;
const { Step } = Steps;

class AddYard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      loading: false,
      currentStep: 0,
      formValues: {},
    };
    this.formRef = React.createRef();
  }

  showDrawer = () => {
    this.setState({ visible: true });
  };

  closeDrawer = () => {
    this.setState({ visible: false, currentStep: 0, formValues: {} });
    this.formRef.current.resetFields();
  };

  next = async () => {
    try {
      const values = await this.formRef.current.validateFields();
      this.setState((prev) => ({
        currentStep: prev.currentStep + 1,
        formValues: { ...prev.formValues, ...values },
      }));
    } catch (error) {
      // validation failed
    }
  };

  prev = () => {
    this.setState((prev) => ({
      currentStep: prev.currentStep - 1,
    }));
  };

onFinish = async () => {
  this.setState({ loading: true });

  try {
    const latestValues = await this.formRef.current.validateFields();
    const allValues = { ...this.state.formValues, ...latestValues };

    // Extract all contact persons, emails, numbers, and extensions
    const contactPersons = [];
    const contactPersonExtensions = [];
    const allEmails = [];
    const allContactNumbers = [];
    const addContactPerson = (person, extension) => {
      const normalizedPerson = person?.trim();
      if (!normalizedPerson) return;

      const normalizedExtension = extension?.trim();
      contactPersons.push(normalizedPerson);
      contactPersonExtensions.push(normalizedExtension || null);
    };

    // Add main contact person if exists
    addContactPerson(allValues.mainContactPerson, allValues.mainContactPersonExtension);
    if (allValues.mainEmail) {
      allEmails.push(allValues.mainEmail);
    }
    if (allValues.mainContactNumber) {
      allContactNumbers.push(allValues.mainContactNumber);
    }

    // Add additional contact persons
    if (allValues.additionalContacts) {
      allValues.additionalContacts.forEach(contact => {
        addContactPerson(contact.person, contact.extension);
        if (contact.email) allEmails.push(contact.email);
        if (contact.number) allContactNumbers.push(contact.number);
      });
    }

const normalizedContactPersonExtensions = contactPersonExtensions.some(Boolean)
  ? contactPersonExtensions
  : undefined;

const payload = {
  yardName: allValues.yardName,
  yardAddress: {
    address: allValues.address,
    street: allValues.street || "",
    city: allValues.city || "",
    state: allValues.state || "",
    country: allValues.country || "USA",
    zip_code: allValues.zipCode || "",
  },
  contactPerson: contactPersons,
  ...(normalizedContactPersonExtensions
    ? { contactPersonExtension: normalizedContactPersonExtensions }
    : {}),
  contactNumbers: allContactNumbers.filter(n => n && n.trim()),
  email: allEmails.filter(e => e && e.trim()),
  link: allValues.link || "",
};
    console.log("Sending payload:", payload);

    const response = await API.createYard(payload);

    if (response?.success) {
      message.success("Yard submitted successfully!");
      this.closeDrawer();

      if (this.props.refreshYards) {
        this.props.refreshYards();
      }
    } else {
      message.error(response.message || "Failed to submit Yard.");
    }
  } catch (error) {
    console.log("❌ Submission failed or validation error", error);
    message.error("Submission failed");
  } finally {
    this.setState({ loading: false });
  }
};

  validatePhoneNumber = (_, value) => {
    if (!value) return Promise.reject("Phone number is required");
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length !== 10) {
      return Promise.reject("Enter a valid 10-digit number");
    }
    return Promise.resolve();
  };

  formatPhoneNumber = (value) => {
    if (!value) return value;
    const cleaned = value.replace(/\D/g, "").slice(0, 10);
    if (cleaned.length < 4) return cleaned;
    if (cleaned.length < 7) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };
validateExtension = (_, value) => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return Promise.resolve();
  const extensionRegex = /^[a-zA-Z0-9\-_]+$/;
  if (!extensionRegex.test(normalizedValue)) {
    return Promise.reject("Extension can only contain letters, numbers, hyphens, and underscores");
  }
  return Promise.resolve();
};
  render() {
    const { visible, currentStep, loading } = this.state;

    return (
      <div>
        <Button type="primary" onClick={this.showDrawer}>
          <PlusOutlined /> Add New Yard
        </Button>

        <Drawer
          title="Add New Yard"
          width={720}
          onClose={this.closeDrawer}
          open={visible}
          bodyStyle={{ paddingBottom: 80 }}
        >
          <Steps current={currentStep} style={{ marginBottom: 24 }}>
            <Step title="Person Info" />
            <Step title="Address Info" />
          </Steps>

          <Form ref={this.formRef} layout="vertical" onFinish={this.onFinish}>
            {currentStep === 0 && (
              <>
                <Form.Item
                  name="yardName"
                  label="Yard Name"
                  rules={[{ required: true, message: "Enter yard name" }]}
                >
                  <Input placeholder="Enter yard name" />
                </Form.Item>

                <Card title="Main Contact Person" size="small" style={{ marginBottom: 16 }}>
                  <Form.Item
                    name="mainContactPerson"
                    label="Contact Person"
                    rules={[{ required: true, message: "Contact person is required" }]}
                  >
                    <Input 
                      placeholder="Enter contact person name" 
                      prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="mainEmail"
                    label="Email"
                    rules={[
                      { required: true, message: "Email is required" },
                      { type: "email", message: "Enter valid email" }
                    ]}
                  >
                    <Input 
                      placeholder="Enter email address" 
                      prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="mainContactNumber"
                    label="Contact Number"
                    rules={[{ required: true, validator: this.validatePhoneNumber }]}
                  >
                    <Input 
                      placeholder="Enter 10-digit number" 
                      maxLength={14}
                      prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                      onChange={(e) => {
                        const formatted = this.formatPhoneNumber(e.target.value);
                        this.formRef.current.setFieldsValue({
                          mainContactNumber: formatted
                        });
                      }}
                    />
                  </Form.Item>

       <Form.Item
  name="mainContactPersonExtension"
  label="Extension (Optional)"
  tooltip="Extension number for this contact person"
  rules={[{ validator: this.validateExtension }]}
>
  <Input 
    placeholder="Enter extension (e.g., ext101, 123)" 
    maxLength={20}
  />
</Form.Item>
                </Card>

                {/* Additional Contacts - Limited to 2 more (total 3) */}
                <Form.List name="additionalContacts">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <Card 
                          key={field.key} 
                          title={`Additional Person ${index + 2}`} 
                          size="small" 
                          style={{ marginBottom: 16 }}
                          extra={
                            <Button 
                              type="text" 
                              danger 
                              icon={<DeleteOutlined />} 
                              onClick={() => remove(field.name)}
                            />
                          }
                        >
                          <Form.Item
                            {...field}
                            name={[field.name, 'person']}
                            label="Contact Person"
                          >
                            <Input 
                              placeholder="Enter contact person name"
                              prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, 'email']}
                            label="Email"
                            rules={[
                              { type: "email", message: "Enter valid email" }
                            ]}
                          >
                            <Input 
                              placeholder="Enter email address"
                              prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                            />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, 'number']}
                            label="Contact Number"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (!value) return Promise.resolve();
                                  const cleaned = value.replace(/\D/g, "");
                                  if (cleaned.length !== 10) {
                                    return Promise.reject("Enter a valid 10-digit number");
                                  }
                                  return Promise.resolve();
                                }
                              }
                            ]}
                          >
                            <Input 
                              placeholder="Enter 10-digit number" 
                              maxLength={14}
                              prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                              onChange={(e) => {
                                const formatted = this.formatPhoneNumber(e.target.value);
                                const additionalContacts = this.formRef.current.getFieldValue('additionalContacts') || [];
                                if (additionalContacts[field.name]) {
                                  additionalContacts[field.name].number = formatted;
                                  this.formRef.current.setFieldsValue({
                                    additionalContacts
                                  });
                                }
                              }}
                            />
                          </Form.Item>
                          <Form.Item
  {...field}
  name={[field.name, 'extension']}
  label="Extension (Optional)"
  tooltip="Extension number for this contact person"
  rules={[{ validator: this.validateExtension }]}  
>
  <Input 
    placeholder="Enter extension (e.g., ext101, 123)" 
    maxLength={20}
  />
</Form.Item>
                        </Card>
                      ))}

                      {fields.length < 2 && (
                        <Form.Item>
                          <Button 
                            type="dashed" 
                            onClick={() => add()} 
                            block 
                            icon={<PlusOutlined />}
                          >
                            Add Person (Max 3 Total)
                          </Button>
                        </Form.Item>
                      )}
                    </>
                  )}
                </Form.List>

                <Form.Item
                  name="link"
                  label="Website Link"
                  rules={[
                    {
                      type: "url",
                      message: "Enter a valid URL (e.g., https://example.com)",
                    },
                  ]}
                >
                  <Input placeholder="Enter website URL (e.g., https://example.com)" />
                </Form.Item>
              </>
            )}

            {currentStep === 1 && (
              <>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[{ required: true, message: "Enter full address" }]}
                >
                  <Input.TextArea placeholder="Enter full address" rows={2} />
                </Form.Item>

                <Form.Item name="Address 1" label="Street">
                  <Input.TextArea placeholder="Enter Address1" />
                </Form.Item>

                <Form.Item name="city" label="City">
                  <Input placeholder="Enter city" />
                </Form.Item>

                <Form.Item name="state" label="State">
                  <Input placeholder="Enter state" />
                </Form.Item>

                <Form.Item
                  name="zipCode"
                  label="Zip Code"
                  rules={[
                    {
                      required: true,
                      pattern: /^\d{5}(-\d{4})?$/,
                      message: "Enter a valid zip code (12345 or 12345-6789)",
                    },
                  ]}
                >
                  <Input placeholder="Enter zip code (e.g., 12345 or 12345-6789)" maxLength={10} />
                </Form.Item>

                <Form.Item name="country" label="Country" initialValue="USA">
                  <Input placeholder="Enter country" />
                </Form.Item>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32 }}>
              {currentStep > 0 && (
                <Button onClick={this.prev} disabled={loading}>
                  Previous
                </Button>
              )}
              {currentStep < 1 && (
                <Button type="primary" onClick={this.next} disabled={loading}>
                  Next
                </Button>
              )}
              {currentStep === 1 && (
                <Button type="primary" htmlType="submit" loading={loading}>
                  Submit
                </Button>
              )}
            </div>
          </Form>
        </Drawer>
      </div>
    );
  }
}

export default AddYard;
